import {Alert, Attributes} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {VehicleSelectionPreviewResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useVehicleSelectionOutcomeFormatter} from '../hooks/useVehicleSelectionOutcomeFormatter';
import {VehicleSelectionPreviewList} from './VehicleSelectionPreviewList';

type PreviewCount = keyof VehicleSelectionPreviewResponseBody['counts'];

const COUNT_LABEL_KEYS: Record<PreviewCount, string> = {
  added: 'entity.newVehicle.labels.added',
  removed: 'entity.newVehicle.labels.removed',
  unchanged: 'entity.promoPhotos.preview.counts.unchanged',
  notEligible: 'entity.promoPhotos.preview.counts.notEligible',
  manuallyRemoved: 'entity.promoPhotos.preview.counts.manuallyRemoved',
  scanned: 'entity.promoPhotos.preview.counts.scanned',
};

const CHANGE_COUNTS: PreviewCount[] = ['added', 'removed', 'unchanged'];
const SCAN_COUNTS: PreviewCount[] = ['notEligible', 'manuallyRemoved', 'scanned'];

interface VehicleSelectionPreviewResultProps extends RequiredTestIdProps {
  preview: VehicleSelectionPreviewResponseBody;
}

export function VehicleSelectionPreviewResult(props: VehicleSelectionPreviewResultProps) {
  const {counts} = props.preview;
  const formatOutcomeValue = useVehicleSelectionOutcomeFormatter();

  // Without automatic assignment the BE attaches nothing: the matches only become available for a
  // manual add on the vehicle.
  const isManualAddOnly = !props.preview.autoAssign && counts.added > 0;

  const toRow = (count: PreviewCount) => ({
    label: i18n.t(COUNT_LABEL_KEYS[count]),
    value: counts[count].toString(),
    testId: count,
  });

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Grid columns={2} spacing={4}>
        <Attributes rows={CHANGE_COUNTS.map(toRow)} data-testid={suffixTestId('changes', props)} />
        <Attributes rows={SCAN_COUNTS.map(toRow)} data-testid={suffixTestId('scan', props)} />
      </Grid>
      <Show when={isManualAddOnly}>
        <Alert
          variant="info"
          message={i18n.t('entity.promoPhotos.preview.labels.manualAddOnly')}
          data-testid={suffixTestId('manualAddOnly', props)}
        />
      </Show>
      <Show when={props.preview.truncated}>
        <Alert
          variant="warning"
          message={i18n.t('entity.promoPhotos.preview.labels.truncated', {scanned: counts.scanned})}
          data-testid={suffixTestId('truncated', props)}
        />
      </Show>
      <Show when={counts.added > 0}>
        <VehicleSelectionPreviewList
          title={i18n.t('entity.promoPhotos.preview.labels.addedList')}
          items={props.preview.added}
          count={counts.added}
          formatOutcomeValue={formatOutcomeValue}
          data-testid={suffixTestId('added', props)}
        />
      </Show>
      <Show when={counts.removed > 0}>
        <VehicleSelectionPreviewList
          title={i18n.t('entity.promoPhotos.preview.labels.removedList')}
          items={props.preview.removed}
          count={counts.removed}
          formatOutcomeValue={formatOutcomeValue}
          data-testid={suffixTestId('removed', props)}
        />
      </Show>
    </VStack>
  );
}
